/**
 * 답례품 스토어 메인 검색·필터 규칙 (화면과 분리된 순수 함수).
 *
 * - 검색어와 가격대는 서로 AND, 가격대 여러 개는 OR.
 * - 가격은 고객이 실제 내는 판매가(priceRetail) 기준.
 * - 분류로 거르는 축은 두지 않는다 (분류 이동은 메인 화면 점프 칩 몫). 분류 이름은 검색어로 찾는다.
 */

export type GiftStoreFilter = {
  keyword: string;
  priceKeys: string[];
};

export const EMPTY_GIFT_STORE_FILTER: GiftStoreFilter = {
  keyword: '',
  priceKeys: [],
};

export type GiftPriceBucket = {
  key: string;
  label: string;
  min: number; // 이상
  max: number; // 미만
};

export const GIFT_PRICE_BUCKETS: GiftPriceBucket[] = [
  { key: 'p0', label: '5천원 미만', min: 0, max: 5000 },
  { key: 'p05', label: '5천~1만원', min: 5000, max: 10000 },
  { key: 'p1', label: '1만원대', min: 10000, max: 20000 },
  { key: 'p2', label: '2만원대', min: 20000, max: 30000 },
  { key: 'p3', label: '3만원대', min: 30000, max: 40000 },
  { key: 'p4', label: '4만원대', min: 40000, max: 50000 },
  { key: 'p5', label: '5만원 이상', min: 50000, max: Infinity },
];

export const getGiftPriceBucketKey = (price: number): string | undefined =>
  GIFT_PRICE_BUCKETS.find((b) => price >= b.min && price < b.max)?.key;

/** 공백·대소문자 무시 비교용 */
export const normalizeGiftKeyword = (value: string): string =>
  value.replace(/\s+/g, '').toLowerCase();

export const isGiftStoreFilterActive = (filter: GiftStoreFilter): boolean =>
  normalizeGiftKeyword(filter.keyword).length > 0 ||
  filter.priceKeys.length > 0;

export const countGiftStoreFilter = (filter: GiftStoreFilter): number =>
  (normalizeGiftKeyword(filter.keyword).length > 0 ? 1 : 0) +
  filter.priceKeys.length;

export type GiftStoreFilterTarget = {
  price: number;
  /** 상품명·브랜드·태그·분류명 등 검색 대상 문자열 */
  texts: (string | null | undefined)[];
};

export const matchesGiftStoreFilter = (
  target: GiftStoreFilterTarget,
  filter: GiftStoreFilter,
): boolean => {
  const keyword = normalizeGiftKeyword(filter.keyword);
  if (keyword) {
    const hit = target.texts.some(
      (text) => !!text && normalizeGiftKeyword(text).includes(keyword),
    );
    if (!hit) return false;
  }
  if (filter.priceKeys.length > 0) {
    const bucket = getGiftPriceBucketKey(target.price);
    if (!bucket || !filter.priceKeys.includes(bucket)) return false;
  }
  return true;
};

export const toggleGiftFilterKey = (keys: string[], key: string): string[] =>
  keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];

/** sessionStorage 등에서 읽은 값을 안전하게 필터로 복원 (모양이 다르면 빈 필터) */
export const parseGiftStoreFilter = (raw: string | null): GiftStoreFilter => {
  if (!raw) return EMPTY_GIFT_STORE_FILTER;
  try {
    const parsed = JSON.parse(raw);
    const strings = (value: unknown): string[] =>
      Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
    return {
      keyword: typeof parsed?.keyword === 'string' ? parsed.keyword : '',
      priceKeys: strings(parsed?.priceKeys),
    };
  } catch {
    return EMPTY_GIFT_STORE_FILTER;
  }
};
